import React, { useState, useEffect } from 'react';
import { Check, Phone, BarChart3, Calendar, Headphones, Star, ArrowRight, MessageSquare, LogIn, Calculator, Clock, Sparkles, Shield, Zap, Users, Globe, X, CreditCard } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ThemeToggle';
import { CreateBusinessDialog } from '../components/CreateBusinessDialog';

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, login, token } = useAuth();
  const [pendingPlan, setPendingPlan] = useState(null);

  const choosePlan = (planId) => {
    if (!isAuthenticated) return navigate(`/signup?plan=${planId}`);
    if (user?.role === 'admin') return navigate('/admin');
    if (user?.business_id) return navigate(`/business/${user.business_id}?plan=${planId}`);
    setPendingPlan(planId);
  };

  const onBusinessCreated = (businessId) => {
    login({ ...user, business_id: businessId }, token);
    navigate(`/business/${businessId}?plan=${pendingPlan}`);
  };

  const closePricing = () => {
    if (user?.business_id) return navigate(`/business/${user.business_id}`);
    if (user?.role === 'admin') return navigate('/admin');
    return navigate('/');
  };

  // Missed call calculator state
  const [missedCalls, setMissedCalls] = useState(5);
  const [leadValue, setLeadValue] = useState(150);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Calculate lost revenue
  const weeklyLoss = missedCalls * leadValue;
  const monthlyLoss = weeklyLoss * 4;
  const yearlyLoss = monthlyLoss * 12;

  const features = [
    { icon: Phone, text: "AI Receptionist 24/7/365" },
    { icon: Clock, text: "400 Minutes Included Monthly" },
    { icon: Phone, text: "Dedicated Local Phone Number" },
    { icon: BarChart3, text: "Call Analytics & Recordings" },
    { icon: Sparkles, text: "Custom AI Personality" },
    { icon: Calendar, text: "Calendar Integration" },
    { icon: Headphones, text: "Priority Support" },
    { icon: Globe, text: "Add more business locations for $2/mo each (own AI number, no extra setup fee)" },
    { icon: Shield, text: "Optional texting add-on: $25/mo" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center shadow-lg shadow-primary/25">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">
              Front<span className="text-primary">DeskPro</span>
            </span>
          </Link>
          
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {!isAuthenticated && (
              <Button variant="ghost" onClick={() => navigate('/login')}>
                <LogIn className="w-4 h-4 mr-2" /> Login
              </Button>
            )}
            {isAuthenticated && (
              <>
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="pricing-logout-btn">
                  <LogIn className="w-4 h-4 sm:mr-1 rotate-180" /><span className="hidden sm:inline">Log out</span>
                </Button>
                <Button variant="outline" size="sm" onClick={closePricing} data-testid="pricing-close-btn" aria-label="Close pricing">
                  <X className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{user?.business_id ? 'Go to Dashboard' : 'Close'}</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Simple, Transparent Pricing
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Simple, Transparent Pricing.
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Choose the plan that fits your business. No hidden fees. Cancel anytime.
          </p>
        </div>
      </div>

      {/* Two Pricing Cards */}
      <div className="px-4 pb-16">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          
          {/* Starter Plan */}
          <Card className="relative overflow-hidden border border-border">
            <div className="p-8">
              {/* Plan Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Starter</h2>
                <p className="text-muted-foreground">Perfect for small businesses</p>
              </div>

              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-foreground">$5</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-sm text-cyan-500 font-medium mt-2">+ $5 one-time signup fee</p>
                <p className="text-xs text-muted-foreground mt-1">150 minutes included</p>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {[
                  "AI Receptionist 24/7/365",
                  "150 Minutes Included",
                  "Local Phone Number",
                  "Call Analytics & Recordings",
                  "Custom AI Personality",
                  "Email Support"
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                    <span className="text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Button 
                onClick={() => choosePlan('starter')}
                variant="outline"
                className="w-full py-6 text-lg"
                data-testid="get-started-starter-btn"
              >
                Get Started <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </Card>

          {/* Pro Plan */}
          <Card className="relative overflow-hidden border-2 border-primary shadow-xl shadow-primary/20">
            {/* Popular Badge */}
            <div className="absolute top-0 right-0 bg-primary text-white px-4 py-1 text-sm font-medium rounded-bl-lg">
              BEST VALUE
            </div>

            <div className="p-8">
              {/* Plan Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Pro</h2>
                <p className="text-muted-foreground">Everything you need for your business</p>
              </div>

              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-foreground">$5</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-sm text-primary font-medium mt-2">+ $5 one-time signup fee</p>
                <p className="text-xs text-muted-foreground mt-1">400 minutes included • Add extra locations for $2/mo each</p>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                    <span className="text-foreground">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Button 
                onClick={() => choosePlan('pro')}
                className="w-full py-6 text-lg bg-primary hover:bg-primary/90"
                data-testid="get-started-btn"
              >
                Get Started Now <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <p className="text-center text-sm text-muted-foreground mt-4">
                Cancel anytime. No long-term contracts.
              </p>
            </div>
          </Card>
        </div>
        
        {/* Usage Rates - Below both cards */}
        <div className="max-w-lg mx-auto mt-8">
          <div className="bg-muted/50 rounded-xl p-4">
            <h4 className="font-semibold text-foreground mb-3 text-center">Usage Rates (Both Plans)</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Text Messages</span>
                <span className="text-foreground font-medium">$25/mo add-on</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Missed Call Calculator */}
      <div className="px-4 py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 flex items-center justify-center gap-3">
              <Calculator className="w-8 h-8 text-primary" />
              Calculate Your Lost Revenue
            </h2>
            <p className="text-muted-foreground">See how much missed calls are costing your business</p>
          </div>

          <Card className="card-modern p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Missed calls per week
                  </label>
                  <Input
                    type="number"
                    value={missedCalls}
                    onChange={(e) => setMissedCalls(Math.max(0, parseInt(e.target.value) || 0))}
                    className="input-industrial text-lg"
                    min="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Average value per lead ($)
                  </label>
                  <Input
                    type="number"
                    value={leadValue}
                    onChange={(e) => setLeadValue(Math.max(0, parseInt(e.target.value) || 0))}
                    className="input-industrial text-lg"
                    min="0"
                  />
                </div>
              </div>

              <div className="bg-red-500/10 rounded-xl p-6 flex flex-col justify-center">
                <p className="text-red-400 text-sm mb-2">You're potentially losing:</p>
                <div className="space-y-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">Weekly</span>
                    <span className="text-2xl font-bold text-red-400">${weeklyLoss.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">Monthly</span>
                    <span className="text-2xl font-bold text-red-400">${monthlyLoss.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-baseline border-t border-red-500/20 pt-2 mt-2">
                    <span className="text-foreground font-medium">Yearly</span>
                    <span className="text-3xl font-bold text-red-500">${yearlyLoss.toLocaleString()}</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Front Desk Pro pays for itself if it saves just {Math.ceil(2 / leadValue)} lead{Math.ceil(2 / leadValue) !== 1 ? 's' : ''} per month.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                q: "What happens if I use more than my included minutes?",
                a: "Your plan includes generous monthly minutes. If you approach your limit, you'll receive alerts so you can upgrade if needed."
              },
              {
                q: "How are text messages billed?",
                a: "Texting is an optional add-on for $25/month. Texts (appointment reminders, confirmations and owner alerts) come from the same number your AI answers. US carriers require a one-time approval that usually takes 3-5 business days."
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes! There are no long-term contracts. Cancel anytime from your dashboard."
              },
              {
                q: "How long does setup take?",
                a: "Most businesses are up and running within 15 minutes. You'll get a dedicated phone number instantly."
              },
              {
                q: "Can I have multiple phone numbers?",
                a: "Yes. On Pro, each extra location (for example a second shop) gets its own AI phone number and settings for $2/month, with no extra setup fee. Minutes are shared across locations."
              }
            ].map((faq, idx) => (
              <Card key={idx} className="card-modern p-6">
                <h3 className="font-semibold text-foreground mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="px-4 py-16 bg-gradient-to-br from-primary/10 to-primary/5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Never Miss a Call Again?
          </h2>
          <p className="text-muted-foreground mb-8">
            Join thousands of businesses using Front Desk Pro to capture every lead.
          </p>
          <Button 
            onClick={() => choosePlan('pro')}
            size="lg"
            className="px-12 py-6 text-lg"
          >
            Start Your Free Trial <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-4 py-8 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © 2026 Front Desk Pro. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
      <CreateBusinessDialog open={!!pendingPlan} onClose={() => setPendingPlan(null)} onCreated={onBusinessCreated} />
    </div>
  );
};

export default Pricing;
